import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import ProductForm, { ProductFormState } from './ProductForm';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { CostingType, type Product, validate as validateProduct } from '../../models/Product';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY, GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import { UPDATE_PRODUCT_MUTATION } from '../../mutations/ProductMutations';
import type { ProductItem } from "../../models/ProductItem";
import { ProductBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';

function ProductEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<ProductFormState['values']>;
  onSubmit: (id: number, formValues: Partial<ProductFormState['values']>, items: ProductItem[], bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => Promise<boolean>;
}) {
  const { productId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<ProductFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ProductFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ProductFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ProductFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ProductFormState['values']>) => {
        const { issues } = validateProduct(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async (
    productFormValues: Partial<ProductFormState['values']>,
    items: ProductItem[],
    bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[] = []) => {
    const { issues } = validateProduct(productFormValues);

    let errors = null;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
    }

    if ((items || []).length === 0) {
      errors = {
        ...errors,
        items: 'You must add at least one item.'
      };
    }

    if (productFormValues.costingType === CostingType.BULK &&
      (bulkPricingMarkupLineItems || []).length === 0) {
      errors = {
        ...errors,
        bulkPricingMarkupLineItems: 'You must add at least one bulk pricing markup line item.'
      };
    }

    if (errors) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const id = +(productId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(
          id,
          productFormValues,
          items,
          bulkPricingMarkupLineItems
        );

        if (success) {
          notifications.show('Product edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/products/${id}`);
        } else {
          notifications.show(
            'Failed to edit product. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit product. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [navigate, notifications, onSubmit, productId, setFormErrors]);

  return (
    <ProductForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onItemFieldChange={() => handleFormFieldChange}
      onBulkPricingMarkupLineItemFieldChange={() => handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/products/${productId}`}
      existingProductId={+(productId || '0')}
    />
  );
}

export default function ProductEdit() {
  const { productId } = useParams();

  const [getProductById] = useLazyQuery(
    GET_PRODUCT_BY_ID_QUERY);

  const [updateProduct] = useMutation(
    UPDATE_PRODUCT_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }, { query: GET_PRODUCT_BY_ID_QUERY, variables: { id: +(productId || '0') } }],
  });

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductByIdError } = await getProductById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getProductByIdError) {
          setError(getProductByIdError);
        }

        if (data?.product) {
          setProduct(data.product);
        } else {
          setError({ message: 'No product found' } as Error);
        }

      } catch (showDataError) {
        setIsLoading(false);
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductById, productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<ProductFormState['values']>, items: ProductItem[], bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => {
      setIsLoading(true);

      const { data: result } = await updateProduct({
        variables: {
          input: {
            id: id,
            name: formValues.name || 'Unnamed Product',
            sku: formValues.sku || null,
            customerId: formValues.customerId || 0,
            costingType: formValues.costingType || CostingType.STANDARD,
            hasInventory: formValues.hasInventory || false,
            items: items.map(i => {
              return {
                id: +i.id,
                itemId: i.itemId,
                productId: id,
                quantity: i.quantity,
                notes: i.notes
              };
            }),
            bulkPricingMarkupLineItems: bulkPricingMarkupLineItems.map(b => {
              return {
                id: +b.id,
                productId: id,
                pounds: b.pounds,
                markup: b.markup
              };
            }),
            matchingProductId: formValues.matchingProductId
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedProduct) {
        setProduct(result.updatedProduct);
        return true;
      }

      return false;
    },
    [updateProduct],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return product ? (
      <ProductEditForm initialValues={product} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, product, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${product?.name || 'N/A'}`}
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: `${product?.name || 'N/A'}`, path: `/dashboard/products/${productId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
